import { createElement } from 'react'
import type { CSSProperties, ReactNode } from 'react'
import type { SeatStatus } from '../data/types'
import { IconCheck, IconProfile } from '../lib/icons'
import { showNotWiredToast } from '../lib/notWiredToast'

declare global {
  // eslint-disable-next-line @typescript-eslint/no-namespace
  namespace JSX {
    interface IntrinsicElements {
      'trumin-logo': React.DetailedHTMLProps<React.HTMLAttributes<HTMLElement>, HTMLElement>
    }
  }
}

// ---------- Button ----------
type ButtonVariant = 'primary' | 'outline' | 'ghost'
export function Button({ children, variant = 'primary', disabled, onClick, full, small }: {
  children: ReactNode; variant?: ButtonVariant; disabled?: boolean; onClick?: () => void; full?: boolean; small?: boolean
}) {
  const styles: Record<ButtonVariant, CSSProperties> = {
    primary: { background: 'var(--accent)', color: 'var(--accent-on)', border: '1px solid transparent' },
    outline: { background: 'transparent', color: 'var(--text)', border: '1px solid var(--hairline-strong)' },
    ghost: { background: 'var(--surface-2)', color: 'var(--text-faint)', border: '1px solid var(--hairline)' },
  }
  return (
    // Unwired actions (no onClick) surface the honest not-wired toast instead
    // of silently doing nothing (Duncan review fix 2; no fabricated flows).
    <button onClick={onClick ?? (() => showNotWiredToast())} disabled={disabled}
      style={{
        ...styles[disabled ? 'ghost' : variant],
        minHeight: small ? 34 : 44, padding: small ? '0 14px' : '0 18px',
        borderRadius: 'var(--r-btn)', fontWeight: 800, fontSize: small ? 12 : 13,
        width: full ? '100%' : undefined, cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        opacity: disabled ? 0.7 : 1, transition: 'filter .12s', whiteSpace: 'nowrap',
      }}>
      {children}
    </button>
  )
}

// ---------- Status badge (event readiness) ----------
export function StatusBadge({ tone, text }: { tone: 'warn' | 'success' | 'neutral'; text: string }) {
  const map = {
    warn: { bg: 'var(--warn-bg)', fg: 'var(--warn)' },
    success: { bg: 'rgba(18,196,172,0.14)', fg: 'var(--accent-soft)' },
    neutral: { bg: 'rgba(255,255,255,0.05)', fg: 'var(--text-faint)' },
  }[tone]
  return <span style={{ background: map.bg, color: map.fg, borderRadius: 'var(--r-pill)', padding: '4px 11px', fontSize: 11.5, fontWeight: 700, whiteSpace: 'nowrap' }}>{text}</span>
}

// ---------- Mono status tag (WE DO THIS / SCHEDULED …) ----------
export function StatusTag({ text }: { text: string }) {
  return <span className="mono" style={{ border: '1px solid var(--hairline-strong)', color: 'var(--text-faint)', borderRadius: 5, padding: '3px 7px', fontSize: 9.5, letterSpacing: '.06em', textTransform: 'uppercase' }}>{text}</span>
}

// ---------- Seat status pill (orders/tickets) ----------
// State-colour semantics per canon §7 [token] (boardroom ruling, fixes the
// pre-canon inversion): amber --warn = unassigned/needs action · blue
// --info-invited = invite pending · teal = assigned/done.
const seatMap: Record<SeatStatus, { label: string; bg: string; fg: string }> = {
  checked_in: { label: 'CHECKED IN', bg: 'rgba(18,196,172,0.15)', fg: 'var(--accent-soft)' },
  assigned: { label: 'ASSIGNED', bg: 'rgba(18,196,172,0.15)', fg: 'var(--accent-soft)' },
  pending: { label: 'PENDING', bg: 'var(--tint-invited)', fg: 'var(--info-invited)' },
  unassigned: { label: 'UNASSIGNED', bg: 'var(--warn-bg)', fg: 'var(--warn)' },
}
export function SeatPill({ status }: { status: SeatStatus }) {
  const m = seatMap[status]
  return <span className="mono" style={{ background: m.bg, color: m.fg, borderRadius: 'var(--r-pill)', padding: '3px 9px', fontSize: 9.5, fontWeight: 700, letterSpacing: '.06em' }}>{m.label}</span>
}

// ---------- Progress bar ----------
export function ProgressBar({ pct }: { pct: number }) {
  return (
    <div style={{ height: 5, borderRadius: 'var(--r-pill)', background: 'rgba(255,255,255,0.08)', overflow: 'hidden' }}>
      <div style={{ height: '100%', width: `${Math.max(0, Math.min(100, pct))}%`, background: 'var(--accent)', borderRadius: 'var(--r-pill)', transition: 'width .3s' }} />
    </div>
  )
}

// ---------- Avatar (line-art person icon — NEVER initials/monogram tiles,
// per the design CLAUDE.md engineering notes; `initials` prop dropped) ----------
export function Avatar({ size = 30, tint }: { size?: number; tint?: string }) {
  return (
    <div aria-hidden style={{ width: size, height: size, borderRadius: '50%', background: tint || 'var(--surface)', border: '1px solid var(--hairline)', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-secondary)', flexShrink: 0 }}>
      <IconProfile size={Math.round(size * 0.55)} />
    </div>
  )
}

// ---------- Brand: the canonical <trumin-logo> lockup (vendored component;
// renders static PNG instantly, upgrades to the live 3D badge) ----------
export function Brand({ size = 28 }: { size?: number }) {
  // The component fills its host's width; height follows the lockup ratio.
  return createElement('trumin-logo', {
    style: { width: Math.round(size * 4.3), display: 'block', flex: 'none' },
    'data-testid': 'trumin-logo',
  })
}

// ---------- Section card ----------
export function Card({ children, style, orgAccent }: { children: ReactNode; style?: CSSProperties; orgAccent?: string }) {
  return (
    <div style={{ background: 'var(--surface)', border: '1px solid var(--hairline-strong)', borderLeft: orgAccent ? `3px solid ${orgAccent}` : undefined, borderRadius: 'var(--r-card)', boxShadow: 'var(--shadow-card)', overflow: 'hidden', ...style }}>
      {children}
    </div>
  )
}

export function DoneTick({ size = 16 }: { size?: number }) {
  return <span style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: size + 6, height: size + 6, borderRadius: '50%', background: 'rgba(18,196,172,0.2)', color: 'var(--accent-soft)' }}><IconCheck size={size} /></span>
}
